import React, { useCallback, useEffect, useMemo } from "react";
import { Alert, Box, Drawer, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import OpenDocuments from "../../noc/OpenDocuments";
import CustomersPackagesGrid from "../../permitComponents/CustomersPackagesGrid";
import { setModalsState } from "../../../store/actions/uiActions/myPermitsActions";
import { isSubAdminEmail } from "../../../functions/helperFunctions";

const CustomersPackages = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const { subAdmins } = useSelector((state) => state.nocData || {});
  const { subAdminDomains = [] } = subAdmins || {};
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const memoizedDrawerContent = useMemo(() => <OpenDocuments />, []);
  const { permitDrawerOpen } = useSelector(
    (state) => state.myPermitsComponent.permits.modalStates,
  );

  const handlePermitDrawerClose = useCallback(() => {
    try {
      const active = document?.activeElement;
      if (active && active instanceof HTMLElement) {
        active.blur();
      }
    } catch {}
    dispatch(setModalsState({ permitDrawerOpen: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!permitDrawerOpen) {
      try {
        const active = document?.activeElement;
        if (active && active instanceof HTMLElement) {
          active.blur();
        }
      } catch {}
    }
  }, [permitDrawerOpen]);

  const isAdmin = currentUser?.user?.role === "admin";
  const isAllowedSubAdmin = isSubAdminEmail(
    currentUser?.user?.email,
    subAdminDomains,
  );

  if (!isAdmin && !isAllowedSubAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          This page is available to admins and allowed sub-admins only.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: isMobile ? "0px" : "1.5rem",
        }}
      >
        <Box sx={{ maxWidth: "1920px", width: "100%", height: "100%" }}>
          <Box
            sx={{
              borderRadius: isMobile ? 0 : "1.5rem",
              boxShadow: "2px 3px 5px #d3d3d3",
              minHeight: "100%",
              ...(isMobile
                ? {
                    height: "100%",
                    maxHeight: "100%",
                  }
                : null),
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                height: "100%",
              }}
            >
              <CustomersPackagesGrid />
            </Box>
          </Box>
        </Box>
      </Box>

      <Drawer
        ModalProps={{
          keepMounted: false,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableRestoreFocus: true,
        }}
        slotProps={{
          root: {
            sx: {
              pointerEvents: permitDrawerOpen ? "auto" : "none",
            },
          },
          backdrop: {
            sx: {
              pointerEvents: permitDrawerOpen ? "auto" : "none",
            },
          },
          paper: {
            sx: {
              width: "100vw",
              overflow: "hidden",
            },
          },
        }}
        anchor="right"
        sx={{ pointerEvents: permitDrawerOpen ? "auto" : "none" }}
        open={permitDrawerOpen}
        onClose={handlePermitDrawerClose}
      >
        {memoizedDrawerContent}
      </Drawer>
    </>
  );
};

export default CustomersPackages;
